import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Image, Upload, Loader2 } from 'lucide-react';

import { PhotoCard } from '@/components/admin/photos/PhotoCard';
import { PhotoFilterBar } from '@/components/admin/photos/PhotoFilterBar';
import { PhotoUploadDialog } from '@/components/admin/photos/PhotoUploadDialog';
import { PhotoEditDialog } from '@/components/admin/photos/PhotoEditDialog';
import { BulkActionBar } from '@/components/admin/photos/BulkActionBar';
import { BulkRepriceDialog } from '@/components/admin/photos/BulkRepriceDialog';
import { BulkReassignDialog } from '@/components/admin/photos/BulkReassignDialog';
import { useBulkPhotoActions } from '@/hooks/useBulkPhotoActions';

import { API } from '@/lib/apiBase';

export default function AdminPhotos() {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState('all');
  const [defaultPrice, setDefaultPrice] = useState(10.0);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [editPhoto, setEditPhoto] = useState(null);
  const [editData, setEditData] = useState({ student_ids: [], price: 10, is_class_photo: false });

  const [bulkData, setBulkData] = useState({
    class_id: '',
    student_id: '',
    is_class_photo: false,
    is_teacher_photo: false,
    price: 10.0,
    files: [],
  });

  // Bulk-action selection state
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkRepriceOpen, setBulkRepriceOpen] = useState(false);
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);

  const toggleSelect = (photoId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedPhotos = useMemo(
    () => photos.filter((p) => selectedIds.has(p.photo_id)),
    [photos, selectedIds],
  );

  // Students available in the currently-selected class (or all)
  const studentsForFilter = useMemo(() => {
    const list = selectedClass === 'all'
      ? students
      : students.filter((s) => s.class_id === selectedClass);
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [students, selectedClass]);

  // Photos filtered by the optional student dropdown
  const visiblePhotos = useMemo(() => {
    if (selectedStudent === 'all') return photos;
    return photos.filter((p) =>
      Array.isArray(p.student_ids) && p.student_ids.includes(selectedStudent),
    );
  }, [photos, selectedStudent]);

  const fetchData = async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const [photosRes, classesRes, studentsRes, settingsRes] = await Promise.all([
        fetch(`${API}/photos${selectedClass !== 'all' ? `?class_id=${selectedClass}` : ''}`, { headers, credentials: 'include' }),
        fetch(`${API}/classes`, { headers, credentials: 'include' }),
        fetch(`${API}/students`, { headers, credentials: 'include' }),
        fetch(`${API}/site-settings`),
      ]);

      if (photosRes.ok) setPhotos(await photosRes.json());
      if (classesRes.ok) setClasses(await classesRes.json());
      if (studentsRes.ok) setStudents(await studentsRes.json());
      if (settingsRes.ok) {
        const s = await settingsRes.json();
        const dp = typeof s.default_photo_price === 'number' ? s.default_photo_price : 10.0;
        setDefaultPrice(dp);
        // If the dialog hasn't been opened yet (price still at hardcoded 10),
        // seed the bulkData price with the admin-configured default.
        setBulkData((prev) => (prev.price === 10.0 ? { ...prev, price: dp } : prev));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setSelectedIds(new Set());
    setSelectedStudent('all');
  }, [selectedClass, getAuthHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkUpload = async (e) => {
    e.preventDefault();

    if (bulkData.files.length === 0 || !bulkData.class_id) {
      toast.error(t('admin.selectPhotos'));
      return;
    }

    // Chunking + concurrency tuning.
    // - UPLOAD_CHUNK_SIZE=10 keeps each POST under the ~100 MB ingress cap.
    // - UPLOAD_CONCURRENCY=2 lets two chunks fly in parallel. Backend processes
    //   files SEQUENTIALLY inside a chunk to bound memory (Pillow decodes are
    //   RAM-heavy — going higher caused 520/OOM on production, see session 38).
    // - MAX_RETRIES retries a failed chunk once with a small backoff so a
    //   single transient 520/503/network hiccup doesn't lose 10 photos.
    const UPLOAD_CHUNK_SIZE = 10;
    const UPLOAD_CONCURRENCY = 2;
    const MAX_RETRIES = 1;
    const RETRY_BACKOFF_MS = 1500;
    setUploading(true);
    const files = bulkData.files;
    setUploadProgress({ done: 0, total: files.length });

    const headers = { ...getAuthHeaders() };
    delete headers['Content-Type'];

    const buildFormData = (chunk) => {
      const fd = new FormData();
      chunk.forEach((f) => fd.append('files', f));
      fd.append('class_id', bulkData.class_id);
      fd.append('is_class_photo', bulkData.is_class_photo);
      fd.append('is_teacher_photo', bulkData.is_teacher_photo);
      fd.append('student_ids', bulkData.student_id || '');
      fd.append('price', bulkData.price);
      return fd;
    };

    // Pre-slice all chunks up-front so workers can pull them off a shared queue.
    const chunks = [];
    for (let i = 0; i < files.length; i += UPLOAD_CHUNK_SIZE) {
      chunks.push(files.slice(i, i + UPLOAD_CHUNK_SIZE));
    }

    const postChunkWithRetry = async (chunk) => {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const response = await fetch(`${API}/photos/upload-bulk`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: buildFormData(chunk),
          });
          // Only retry on origin-side transient issues, not 4xx (client bug).
          if (response.status >= 500 && attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS));
            continue;
          }
          return response;
        } catch (err) {
          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS));
            continue;
          }
          throw err;
        }
      }
      return null;
    };

    let totalUploaded = 0;
    let totalErrors = 0;
    let doneCount = 0;
    let nextChunkIdx = 0;

    const worker = async () => {
      while (true) {
        const myIdx = nextChunkIdx++;
        if (myIdx >= chunks.length) return;
        const chunk = chunks[myIdx];
        try {
          const response = await postChunkWithRetry(chunk);
          if (!response || !response.ok) {
            totalErrors += chunk.length;
          } else {
            const result = await response.json();
            totalUploaded += result.total_uploaded || 0;
            totalErrors += result.total_errors || 0;
          }
        } catch (err) {
          totalErrors += chunk.length;
        }
        doneCount += chunk.length;
        setUploadProgress({ done: doneCount, total: files.length });
      }
    };

    try {
      // Spin up workers, cap at chunk count so we never over-parallelise a tiny upload.
      const workerCount = Math.min(UPLOAD_CONCURRENCY, chunks.length);
      await Promise.all(Array.from({ length: workerCount }, worker));

      toast.success(
        `${totalUploaded} ${t('admin.photosUploaded')}${totalErrors > 0 ? ` (${totalErrors} errors)` : ''}`,
      );
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setUploading(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setBulkData({ class_id: '', student_id: '', is_class_photo: false, is_teacher_photo: false, price: 10.0, files: [] });
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm(`${t('common.confirm')}?`)) return;

    try {
      const headers = { ...getAuthHeaders() };
      const response = await fetch(`${API}/photos/${photoId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to delete');
      toast.success(t('common.success'));
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const openEditDialog = (photo) => {
    setEditPhoto(photo);
    setEditData({
      student_ids: photo.student_ids || [],
      price: photo.price || 10,
      is_class_photo: photo.is_class_photo || false,
    });
  };

  const handleSaveEdit = async () => {
    if (!editPhoto) return;
    try {
      const response = await fetch(`${API}/photos/${editPhoto.photo_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editData),
      });
      if (!response.ok) throw new Error('Failed to update');
      toast.success(t('common.success'));
      setEditPhoto(null);
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const toggleEditStudent = (studentId) => {
    setEditData((prev) => ({
      ...prev,
      student_ids: prev.student_ids.includes(studentId)
        ? prev.student_ids.filter((id) => id !== studentId)
        : [...prev.student_ids, studentId],
    }));
  };

  const { bulkSaving, bulkDelete, bulkReprice, bulkReassign } = useBulkPhotoActions({
    onSuccess: () => { clearSelection(); fetchData(); },
  });

  const handleBulkDelete = () => bulkDelete(Array.from(selectedIds));

  const handleBulkReprice = async (newPrice) => {
    const result = await bulkReprice(Array.from(selectedIds), newPrice);
    if (result) setBulkRepriceOpen(false);
  };

  const handleBulkReassign = async (payload) => {
    const result = await bulkReassign(Array.from(selectedIds), payload);
    if (result) setBulkReassignOpen(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12" data-testid="admin-photos">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('admin.managePhotos')}</h1>
          <p className="text-muted-foreground">{t('admin.managePhotosDesc')}</p>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <PhotoFilterBar
            classes={classes}
            studentsForFilter={studentsForFilter}
            selectedClass={selectedClass}
            onClassChange={setSelectedClass}
            selectedStudent={selectedStudent}
            onStudentChange={setSelectedStudent}
          />

          <PhotoUploadDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            classes={classes}
            students={students}
            bulkData={bulkData}
            setBulkData={setBulkData}
            uploading={uploading}
            uploadProgress={uploadProgress}
            onSubmit={handleBulkUpload}
            triggerDisabled={classes.length === 0}
            defaultPrice={defaultPrice}
          />
        </div>
      </div>

      {classes.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Image className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('admin.createClassesFirst')}</h3>
          <p className="text-muted-foreground">{t('admin.createClassesFirstDesc')}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : visiblePhotos.length === 0 && classes.length > 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Image className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('admin.noPhotos')}</h3>
          <p className="text-muted-foreground mb-6">{t('admin.noPhotosDesc')}</p>
          <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary/90">
            <Upload className="w-4 h-4 mr-2" />{t('admin.uploadPhotos')}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photos-grid">
          {visiblePhotos.map((photo) => (
            <PhotoCard
              key={photo.photo_id}
              photo={photo}
              students={students}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              selected={selectedIds.has(photo.photo_id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}

      <PhotoEditDialog
        editPhoto={editPhoto}
        onClose={() => setEditPhoto(null)}
        editData={editData}
        setEditData={setEditData}
        students={students}
        onSave={handleSaveEdit}
        onToggleStudent={toggleEditStudent}
      />

      <BulkActionBar
        selectedCount={selectedIds.size}
        onClear={clearSelection}
        onDelete={handleBulkDelete}
        onReprice={() => setBulkRepriceOpen(true)}
        onReassign={() => setBulkReassignOpen(true)}
      />

      <BulkRepriceDialog
        open={bulkRepriceOpen}
        onOpenChange={setBulkRepriceOpen}
        selectedCount={selectedIds.size}
        saving={bulkSaving}
        onApply={handleBulkReprice}
      />

      <BulkReassignDialog
        open={bulkReassignOpen}
        onOpenChange={setBulkReassignOpen}
        selectedPhotos={selectedPhotos}
        students={students}
        saving={bulkSaving}
        onApply={handleBulkReassign}
      />
    </div>
  );
}
